import { useEffect, useMemo, useState } from 'react';
import { fieldReader, fieldWriter } from '../../services/loanApi';
import { getAccessToken } from '../../services/authApi';
import { getZipLocations } from '../../services/locationServiceApi';
import US_STATES from '../../data/usStates';
import Toast from '../shared/Toast';
import LoadingOverlay from '../shared/LoadingOverlay';
import { menuItemsCP, menuItemsBP } from '../menu/menuItems';
import { collectConditionFields, evaluateCompositeCondition, isFieldEffectivelyEmpty } from '../../utils/conditionUtils';
import { runLicensingGateForPage } from '../../utils/licenseValidation';
import { getLockStatusNote, isPollyProcessingStatus } from '../../utils/pricingValidation';
import elliemaeApi from '../../services/interceptors/elliemaeApi';
import { getServiceOrders, hasFulfilledClosingFeeOrder } from '../../services/orderService';
import { resolveFieldOptions, getBlockedLoanProgramName } from '../../utils/loanProgramUtil';

const DisclosurePageRenderer = ({ pageName, config, redirectPage }) => {
    // auth + loan
    const [token, setToken] = useState(null);
    const [loanId, setLoanId] = useState('');

    // data
    const [fieldValues, setFieldValues] = useState({});
    const [pageConfig, setPageConfig] = useState(null);

    // ui
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');

    // toast (animated like your original)
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState('animate-fade-in-right');

    // zip popup
    const [showZipPopup, setShowZipPopup] = useState(false);
    const [cityOptions, setCityOptions] = useState([]);
    const [zipSelection, setZipSelection] = useState({ cityId: '', stateId: '' });

    // lock comparison state (only when 4788 === "Needs Validation")
    const [lcFields, setLcFields] = useState([]);
    const [lcStatus, setLcStatus] = useState('');
    const [lcLockDate, setLcLockDate] = useState('');
    const [lcExpire, setLcExpire] = useState('');
    const [licenseStatus, setLicenseStatus] = useState({
        blocked: false,
        companyRequired: false,
        loRequired: false,
        companyHas: false,
        loHas: false,
        stateCode: '',
        stateName: '',
        reason: ''
    });
    const [closingFeeOrderFulfilled, setClosingFeeOrderFulfilled] = useState(true);
    const [skipModuleValidations, setSkipModuleValidations] = useState(false);

    const continueDisclosureButtonText =
        licenseStatus?.brokerSelection === 'RequestLEAndDisclosures'
            ? 'Continue to Request Disclosures'
            : 'Continue to Send Disclosures';

    // helpers
    const isEmptyField = (fieldId, val) => {
        return isFieldEffectivelyEmpty(fieldId, val);
    };

    const getBorrowerPairCount = (values) => {
        const raw = values["4460"];
        const count = parseInt(raw, 10);
        return Number.isFinite(count) && count > 0 ? count : 1;
    };

    const getMissingOrderCreditPairFields = (values) => {
        const pairCount = getBorrowerPairCount(values);
        const missing = [];

        for (let pairIndex = 1; pairIndex <= pairCount; pairIndex++) {
            const fieldId = `300#${pairIndex}`;
            if (isEmptyField(fieldId, values[fieldId])) {
                missing.push(fieldId);
            }
        }

        return missing;
    };

    const isOrderCreditCleared = (values) => {
        return getMissingOrderCreditPairFields(values).length === 0;
    };

    // returns true if a field fails its JSON "validate" rule
    const isInvalidByValidate = (field) => {
        if (!field?.validate) return false;
        const current = (fieldValues[field.id] ?? '').toString().trim();
        if (field.validate === 'notTBD') {
            return current.toUpperCase() === 'TBD';
        }
        return false;
    };

    const normalizeDate = (date) =>
        new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const showToastMsg = (message) => {
        setToastMessage(message);
        setShowToast(true);
    };

    useEffect(() => {
        let hideTimer, vanishTimer;
        if (showToast) {
            setToastVisible(true);
            setToastAnimation('animate-fade-in-right');
            hideTimer = setTimeout(() => {
                setToastAnimation('animate-fade-out-right');
                vanishTimer = setTimeout(() => {
                    setShowToast(false);
                    setToastVisible(false);
                }, 500);
            }, 4000);
        }
        return () => {
            clearTimeout(hideTimer);
            clearTimeout(vanishTimer);
        };
    }, [showToast]);

    // init
    useEffect(() => {
        const init = async () => {
            try {
                window.elli?.script?.guest?.create?.();

                const loanObject = await window.elli.script.getObject('loan');
                const loan = await loanObject.all();
                setLoanId(loan.id);

                const authObject = await window.elli.script.getObject('auth');
                const authCode = await authObject.createAuthCode();
                const accessToken = await getAccessToken(authCode);
                setToken(accessToken);

                // collect base field ids from JSON Fields
                const baseFieldIds = config.FieldGroups.flatMap((group) =>
                    (group.Fields?.filter((f) => f.id).map((f) => f.id)) || []
                );
                // ALSO collect ShowIf field ids so we can evaluate visibility
                const showIfFieldIds = config.FieldGroups
                    .map(g => g.ShowIf?.field)
                    .filter(Boolean);

                // build ids to read (loan type + 4788 + all base fields)
                const fieldIds = [
                    ...new Set([
                        'CX.LOAN.TYPE',
                        '4788', // Lock Validation status (also used as button id)
                        '3238',
                        'LOCKRATE.CURRENTSTATUS',
                        '230',
                        '231',
                        'LCP.X1',
                        'LCP.X8',
                        'CX.LEGAL.ENTITY.TYPE',
                        "4460",
                        "300#2",
                        "300#3",
                        "300#4",
                        "300#5",
                        "CX.TPOC.OVERRIDE.VALIDATIONS",
                        ...baseFieldIds,
                        ...showIfFieldIds,
                        ...config.FieldGroups.flatMap(group => collectConditionFields(group.NotRequiredIf))
                    ])
                ];

                const values = await fieldReader(accessToken, fieldIds, loan.id);

                const loanType = values['CX.LOAN.TYPE'];
                const stateCode = (values['14'] || '').trim();
                const tpoOrgId = (values['TPO.X15'] || '').trim();
                const tpoUserId = (values['TPO.X62'] || '').trim();
                const city = (values['12'] || '').trim();
                const legalEntityType = (values['CX.LEGAL.ENTITY.TYPE'] || '').trim();

                const overrideValidations =
                    (values['CX.TPOC.OVERRIDE.VALIDATIONS'] || '')
                        .toString()
                        .trim()
                        .toUpperCase();

                const skipModules = overrideValidations === 'Y';
                setSkipModuleValidations(skipModules);

                let filteredGroups = config.FieldGroups.filter(
                    (g) => !g.LoanType || g.LoanType === loanType
                );

                if (skipModules) {
                    filteredGroups = filteredGroups.filter(
                        (g) => (g.Type || '').toLowerCase() !== 'module'
                    );
                }

                // Run licensing gate and store result
                let status = {
                    blocked: false,
                    companyRequired: false,
                    loRequired: false,
                    companyHas: false,
                    loHas: false,
                    stateCode: '',
                    stateName: '',
                    reason: ''
                };

                if (!skipModules) {
                    status = await runLicensingGateForPage(
                        loanType,
                        stateCode,
                        tpoOrgId,
                        tpoUserId,
                        city,
                        legalEntityType
                    );
                }

                setLicenseStatus({ ...status, city });

                let closingFeeFulfilled = true;

                if (!skipModules && (loanType || '').trim() === 'Consumer') {
                    try {
                        const serviceOrders = await getServiceOrders(accessToken, loan.id);
                        closingFeeFulfilled = hasFulfilledClosingFeeOrder(serviceOrders);
                    } catch (err) {
                        console.error('Closing Fee service order check failed:', err);
                        closingFeeFulfilled = false;
                    }
                }

                setClosingFeeOrderFulfilled(closingFeeFulfilled);

                // If Business loan, hide "Commerical Loan App" group when the document already exists
                // if ((loanType || '').trim() === 'Business') {
                //     // Find the Business group (by name) and read its 'document' field from JSON
                //     const businessGroup = filteredGroups.find(g => g.Group === 'Commerical Loan App');
                //     const businessDocField = businessGroup?.Fields?.find(f => f.document);
                //     const docTitle = businessDocField?.document;

                //     if (docTitle) {
                //         const docId = await checkDocumentByAssignedEntity(accessToken, loan.id, docTitle);
                //         const businessDocMissing = !docId;

                //         // If document exists, remove the "Commerical Loan App" group from the page
                //         if (!businessDocMissing) {
                //             filteredGroups = filteredGroups.filter(g => g.Group !== 'Commerical Loan App');
                //         }
                //     }
                // }

                if (status.blocked) {
                    filteredGroups = [
                        ...filteredGroups.filter(g => g.Group === 'Licensing'),
                        ...filteredGroups.filter(g => g.Group !== 'Licensing'),
                    ];
                }

                setFieldValues(values);
                setPageConfig({ ...config, FieldGroups: filteredGroups });
            } catch (error) {
                console.error(`Error initializing ${pageName} page:`, error);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);



    // fetch lockComparison only when 4788 === "Needs Validation"
    useEffect(() => {
        const needs = (fieldValues['4788'] || '').trim() === 'Needs Validation';
        if (!needs || !loanId || !token) return;

        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await elliemaeApi.get(
                    `/secondary/v1/loans/${loanId}/lockComparison`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                if (cancelled) return;
                setLcFields(data.fields || []);
                setLcStatus(data.lockValidationStatus || '');
                setLcLockDate(data.lockDate || '');
                setLcExpire(data.lockExpirationDate || '');
            } catch (e) {
                console.error('Lock comparison fetch failed:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [fieldValues['4788'], loanId, token]);

    // visibility rules
    const shouldShowGroup = (group) => {
        if (skipModuleValidations && group.Type === 'Module') {
            return false;
        }

        // Show Licensing group only when the gate fails
        if (group.Group === 'Licensing') {
            return !skipModuleValidations && licenseStatus.blocked;
        }

        if (group.Group === 'Order Credit') {
            if (isGroupNotRequired(group, fieldValues)) return false;
            return !isOrderCreditCleared(fieldValues);
        }

        if (group.Group === 'Closing Fee') {
            const loanType = (fieldValues['CX.LOAN.TYPE'] || '').trim();
            return !skipModuleValidations && loanType === 'Consumer' && !closingFeeOrderFulfilled;
        }

        // Special: Pricing should show when:
        // - Order Credit is completed (all borrower pair credit fields are filled) OR
        // - Order Credit is NOT required (its NotRequiredIf condition is satisfied)
        if (group.Group === 'Pricing') {
            const orderCreditGroup =
                (pageConfig?.FieldGroups || config?.FieldGroups || []).find(g => g.Group === 'Order Credit');

            const orderCreditNotRequired =
                orderCreditGroup?.NotRequiredIf
                    ? evaluateCompositeCondition(orderCreditGroup.NotRequiredIf, fieldValues)
                    : false;

            return isOrderCreditCleared(fieldValues) || orderCreditNotRequired;
        }

        // LO Comp Not Configured (Consumer + Lender Paid + LCP.X8 empty) => block Send/Request Disclosures
        if (group.Group === 'LO Comp Not Configured') {
            if (skipModuleValidations) return false;

            const loanType = (fieldValues['CX.LOAN.TYPE'] || '').toString().trim().toLowerCase();
            const loCompType = (fieldValues['LCP.X1'] || '').toString().trim();
            const loCompBasePlanTotal = (fieldValues['LCP.X8'] || '').toString().trim();

            const isConsumer = loanType === 'consumer';
            const isLenderPaid =
                loCompType.localeCompare('Lender Paid', undefined, { sensitivity: 'accent' }) === 0;

            return isConsumer && isLenderPaid && isFieldEffectivelyEmpty('LCP.X8', loCompBasePlanTotal);
        }

        // Loan program blocked for this org (ChampsTPO: ACTIVATOR PRIME) => block Send/Request Disclosures and Submit Loan
        if (group.Group === 'Loan Program Not Available') {
            return !skipModuleValidations &&
                Boolean(getBlockedLoanProgramName(fieldValues[group.ShowIf?.field], fieldValues['1401']));
        }

        // Special: Lock Comparison only when 4788 === "Needs Validation"
        if (group.Group === 'Lock Comparison') {
            return !skipModuleValidations &&
                (fieldValues['4788'] ?? '').toString().trim() === 'Needs Validation';
        }

        if (group.Group === 'Rate Lock — Processing') {
            return !skipModuleValidations &&
                isPollyProcessingStatus(
                    fieldValues['LOCKRATE.CURRENTSTATUS'],
                    fieldValues['1401'],
                    fieldValues['CX.LOAN.TYPE']
                );
        }

        // Special: Mortgage Fees should show ONLY when Borrower Paid AND 389 is empty
        if (group.Group === 'Mortgage Fees') {
            const isBorrowerPaid =
                (fieldValues['LCP.X1'] || '').trim().toLowerCase() === 'borrower paid';
            if (!isBorrowerPaid) return false;

            // If 389 already has a value, don't show the group
            const feeEmpty = isEmptyField('389', fieldValues['389']);
            return feeEmpty;
        }

        if (group.Group === 'Insurance and Fees') {
            const homeownerAmountFilled = !isEmptyField('230', fieldValues['230']);
            const homeownerMonthsFilled = !isEmptyField('1387', fieldValues['1387']);
            const mortgageAmountFilled = !isEmptyField('231', fieldValues['231']);
            const mortgageMonthsFilled = !isEmptyField('1386', fieldValues['1386']);

            const homeownerSatisfied = !homeownerAmountFilled || (homeownerAmountFilled && homeownerMonthsFilled);
            const mortgageSatisfied = !mortgageAmountFilled || (mortgageAmountFilled && mortgageMonthsFilled);

            if (homeownerSatisfied && mortgageSatisfied) {
                return false;
            }
            //hide the group
            return true;
        }

        if (group.Group === 'Processing Fee Warning') {
            // 1) BOTH empty => show warning
            // 2) BOTH filled => show hardstop/conflict message
            // 3) exactly one filled => hide
            return processingFeeBothEmpty || processingFeeBothFilled;
        }

        // Generic ShowIf handling
        const condition = group.ShowIf;
        if (condition) {
            const raw = fieldValues[condition.field];
            const val = (raw ?? '').toString().trim();

            // generic string equals (case-insensitive)
            if (condition.operator === 'equals') {
                const target = (condition.value ?? '').toString().trim();
                return val.localeCompare(target, undefined, { sensitivity: 'accent' }) === 0;
            }

            // simple empty/notEmpty
            if (condition.operator === 'notEmpty') return val !== '';
            if (condition.operator === 'empty') return val === '';

            // date vs today
            const fieldDateObj = normalizeDate(new Date(val));
            const todayObj = normalizeDate(new Date());
            if (isNaN(fieldDateObj)) return true;

            switch (condition.operator) {
                case 'lt':
                    return fieldDateObj < todayObj;
                case 'lte':
                    return fieldDateObj <= todayObj;
                case 'eq':
                    return fieldDateObj.getTime() === todayObj.getTime();
                case 'gte':
                    return fieldDateObj >= todayObj;
                case 'gt':
                    return fieldDateObj > todayObj;
                default:
                    return false;
            }
        }

        // For Field groups without ShowIf:
        // show if any visible field is empty OR fails validation (e.g., notTBD)
        if (group.Type === 'Field') {
            const needsAttention = group.Fields?.some((f) => {
                if (f.hidden) return false;
                const v = fieldValues[f.id];
                return isEmptyField(f.id, v) || isInvalidByValidate(f);
            });
            return !!needsAttention;
        }

        // Modules without ShowIf default to show
        return true;
    };

    const isGroupNotRequired = (group) =>
        group?.NotRequiredIf ? evaluateCompositeCondition(group.NotRequiredIf, fieldValues) : false;

    const isModuleGroupCleared = (group) => {
        if (group.Group === 'Licensing' && licenseStatus.blocked) return false;

        // Order Credit: cleared only when all borrower pair credit fields are filled
        if (group.Group === "Order Credit") {
            if (isGroupNotRequired(group, fieldValues)) return true;
            return isOrderCreditCleared(fieldValues);
        }

        if (group.Group === 'Closing Fee') {
            return closingFeeOrderFulfilled;
        }

        // Lock Comparison is never considered "cleared" by field fill
        if (group.Group === 'Lock Comparison') return false;

        if (group.Group === 'Rate Lock — Processing') return false;

        if (isGroupNotRequired(group)) return true;

        if (!group.Fields?.length) return false;
        return group.Fields.every((field) => {
            const value = fieldValues[field.id];
            return !isEmptyField(field.id, value);
        });
    };

    const allModulesCleared = useMemo(() => {
        if (!pageConfig) return false;
        return pageConfig.FieldGroups
            .filter((group) => group.Type === 'Module')
            .filter((group) => shouldShowGroup(group))
            .every((group) => isModuleGroupCleared(group));
    }, [pageConfig, fieldValues, closingFeeOrderFulfilled]);

    const processingFeeFields = [
        { id: 'NEWHUD.X139', label: 'NEWHUD.X139' },
        { id: '1621', label: '1621' }
    ];

    const isProcessingFeeBothEmpty = () => {
        const inhouseEmpty = isEmptyField('1621', fieldValues['1621']);
        const thirdPartyEmpty = isEmptyField('NEWHUD.X139', fieldValues['NEWHUD.X139']);
        return inhouseEmpty && thirdPartyEmpty;
    };

    const isProcessingFeeBothFilled = () => {
        const inhouseEmpty = isEmptyField('1621', fieldValues['1621']);
        const thirdPartyEmpty = isEmptyField('NEWHUD.X139', fieldValues['NEWHUD.X139']);
        return !inhouseEmpty && !thirdPartyEmpty;
    };

    const processingFeeBothEmpty = isProcessingFeeBothEmpty();
    const processingFeeBothFilled = isProcessingFeeBothFilled();


    const visibleFieldGroups = useMemo(() => {
        return (pageConfig?.FieldGroups || [])
            .filter(g => g.Type === 'Field')
            .filter(g => shouldShowGroup(g));
    }, [pageConfig, fieldValues, licenseStatus, lcFields, lcStatus, lcLockDate, lcExpire]);

    const otherFieldGroups = useMemo(() => {
        return visibleFieldGroups.filter(g => g.Group !== 'Processing Fee Warning');
    }, [visibleFieldGroups]);

    const hasOtherFieldIssues = useMemo(() => {
        // "issues" = any other visible Field-group has empty/invalid visible fields
        return otherFieldGroups.some(group =>
            (group.Fields || [])
                .filter(f => !f.hidden)
                .some(f => isEmptyField(f.id, fieldValues[f.id]) || isInvalidByValidate(f))
        );
    }, [otherFieldGroups, fieldValues]);


    const processingFeeOnlyScenario =
        processingFeeBothEmpty &&
        visibleFieldGroups?.length === 1 &&
        visibleFieldGroups?.[0]?.Group === 'Processing Fee Warning';

    // Continue allowed ONLY for warning (both empty), NOT for conflict (both filled)
    const canContinueToSendDisclosures = processingFeeOnlyScenario;


    const showAllFieldsRequiredNote = useMemo(() => {
        // if there are no visible field groups, don't show it
        if (!visibleFieldGroups?.length) return false;

        // if the only visible field group is Processing Fee Warning, hide the note
        if (
            visibleFieldGroups.length === 1 &&
            visibleFieldGroups[0].Group === 'Processing Fee Warning'
        ) {
            return false;
        }

        // otherwise show it (means some real required field(s) are missing)
        return true;
    }, [visibleFieldGroups]);


    const handleZipBlur = async (field) => {
        if (field.datatype !== 'Zip') return;
        const zip = document.getElementById(`field-${field.id}`)?.value;
        if (!zip || zip.length < 5) return;

        const results = await getZipLocations(zip, token);
        const { city: cityId, state: stateId } = field;

        if (results.length === 1) {
            document.getElementById(`field-${cityId}`).value = results[0].city;
            document.getElementById(`field-${stateId}`).value = results[0].abbrev;
        } else if (results.length > 1) {
            setCityOptions(results);
            setZipSelection({ cityId, stateId });
            setShowZipPopup(true);
        }
    };

    // allow only digits, one leading -, and one dot; clamp to range; fix precision
    const normalizeDecimal = (raw, { min = 0, max = 100, decimals = 3 } = {}) => {
        let s = String(raw ?? '').trim();

        // strip everything except digits, dot, minus
        s = s.replace(/[^\d.-]/g, '');

        // only one minus at start
        s = s.replace(/(?!^)-/g, '');

        // only one dot
        s = s.replace(/(\..*)\./g, '$1');

        if (s === '' || s === '-' || s === '.') return '';

        let n = Number(s);
        if (Number.isNaN(n)) return '';

        // clamp to [min, max]
        if (n < min) n = min;
        if (n > max) n = max;

        // fix precision safely
        const pow = Math.pow(10, decimals);
        n = Math.round(n * pow) / pow;

        // keep trailing zeros consistent with decimals user typed; default to up to `decimals`
        return n.toString();
    };

    // live keystroke guard for decimal fields
    const filterDecimalKeystrokes = (value) =>
        value
            .replace(/[^\d.-]/g, '')       // only digits, dot, minus
            .replace(/(?!^)-/g, '')        // minus only at start
            .replace(/(\..*)\./g, '$1');   // only one dot

    const isFieldCurrentlyRequired = (field, uiValue, allFieldValues) => {
        if (field.requiredIf && field.requiredIf.field) {
            const triggerId = field.requiredIf.field;
            const triggerOp = field.requiredIf.operator;
            const triggerVal = (document.getElementById(`field-${triggerId}`)?.value ?? allFieldValues[triggerId] ?? '').toString().trim();

            if (triggerOp === 'notEmpty') {
                return triggerVal !== '';
            }
            if (triggerOp === 'empty') {
                return triggerVal === '';
            }
        }

        return true;
    };

    const isZeroLikeValue = (val) => {
        if (val === null || val === undefined) return false;

        const trimmed = String(val).trim();
        if (trimmed === '') return false;

        const normalized = trimmed.replace(/,/g, '');
        const num = Number(normalized);
        return Number.isNaN(num) || num <= 0;
    };

    const handleSave = async () => {
        const visibleFieldGroups = pageConfig?.FieldGroups
            .filter((group) => group.Type === 'Field')
            .filter((group) => shouldShowGroup(group));

        const updated = [];
        const missingFields = [];
        let atLeastOneFilled = false;

        const isProcessingFeeField = (id) => id === 'NEWHUD.X139' || id === '1621';
        const isProcessingFeeGroup = (groupName) => groupName === 'Processing Fee Warning';

        // compute UI-side PF state ONCE (fresh values)
        const uiInhouse = (document.getElementById('field-1621')?.value ?? fieldValues['1621'] ?? '').toString().trim();
        const uiThirdParty = (document.getElementById('field-NEWHUD.X139')?.value ?? fieldValues['NEWHUD.X139'] ?? '').toString().trim();

        const uiInhouseBlank = uiInhouse === '';
        const uiThirdPartyBlank = uiThirdParty === '';

        const uiInhouseZero = isZeroLikeValue(uiInhouse);
        const uiThirdPartyZero = isZeroLikeValue(uiThirdParty);

        const uiBothActuallyEmpty = uiInhouseBlank && uiThirdPartyBlank;

        const uiInhouseHasValidValue = !uiInhouseBlank && !uiInhouseZero;
        const uiThirdPartyHasValidValue = !uiThirdPartyBlank && !uiThirdPartyZero;
        const uiBothFilled = uiInhouseHasValidValue && uiThirdPartyHasValidValue;

        if ((!uiInhouseBlank && uiInhouseZero) || (!uiThirdPartyBlank && uiThirdPartyZero)) {
            showToastMsg(
                'You have not entered a Processing Fee or a Third Party Processing Fee, enter an amount greater than 0, or leave both fields blank, if a Processing fee will not be charged.'
            );
            return;
        }

        visibleFieldGroups?.forEach((group) => {
            group.Fields.forEach((field) => {
                if (field.hidden) return;

                const el = document.getElementById(`field-${field.id}`);
                let uiValue = el?.value?.trim();
                const storedValue = (fieldValues[field.id] ?? '').toString().trim();

                if (field.datatype === 'decimal') {
                    const money = isMoneyField(field.id);

                    const min = typeof field.min === 'number' ? field.min : 0;
                    const max = typeof field.max === 'number'
                        ? field.max
                        : (money ? 999999999 : 100);

                    const decimals = typeof field.decimals === 'number'
                        ? field.decimals
                        : (money ? 2 : 3);

                    uiValue = normalizeDecimal(uiValue, { min, max, decimals });
                }

                const effectiveValue = uiValue !== undefined ? uiValue : storedValue;

                let requiredNow = isFieldCurrentlyRequired(field, uiValue, fieldValues);

                // Processing fee fields are NOT required (warning-only fields)
                if (isProcessingFeeField(field.id)) {
                    requiredNow = false;
                }

                const inProcessingFeeGroup = isProcessingFeeGroup(group.Group);
                const isPF = isProcessingFeeField(field.id);

                // Processing Fee fields: allow clearing and saving
                if (inProcessingFeeGroup && isPF) {
                    if (uiValue !== undefined && uiValue !== storedValue) {
                        updated.push({ id: field.id, value: uiValue }); // uiValue can be ""
                        atLeastOneFilled = true; // IMPORTANT: user made a change
                    }
                    return; // don’t validate missingFields for PF fields
                }

                // Normal behavior for other fields
                if (requiredNow && isEmptyField(field.id, effectiveValue)) {
                    missingFields.push(field.name);
                } else if (!requiredNow && isEmptyField(field.id, effectiveValue)) {
                    // not required AND empty -> don't flag
                } else {
                    atLeastOneFilled = true;
                    if (uiValue !== undefined && uiValue !== storedValue) {
                        let finalValue = uiValue;

                        // Block zero for Processing Fee fields
                        if (
                            (field.id === '1621' || field.id === 'NEWHUD.X139') &&
                            isFieldEffectivelyEmpty(field.id, uiValue)
                        ) {
                            finalValue = ''; // treat 0 as empty
                        }

                        updated.push({ id: field.id, value: finalValue });
                    }
                }

                // application date special check
                if (field.id === '745') {
                    const normalizedUI = (uiValue ?? '').trim();
                    const today = new Date().toISOString().split('T')[0];
                    if (!normalizedUI) {
                        missingFields.push('Application Date is required.');
                    } else if (normalizedUI > today) {
                        missingFields.push('Application Date cannot be a future date.');
                    }
                }

                if (field.validate === 'notTBD') {
                    const v = (uiValue ?? storedValue ?? '').trim().toUpperCase();
                    if (v === 'TBD') {
                        missingFields.push(`${field.name} cannot be TBD.`);
                    }
                }
            });
        });

        // HARDSTOP: both filled (conflict) — block until user clears one
        if (uiBothFilled) {
            showToastMsg(
                "You cannot have a Processing Fee and a Third Party Processing Fee. Please remove one before proceeding to disclosures. Processing payable to your company. Third Party Processing is paid to an outside processing company."
            );
            return;
        }

        const processingFeeOnlyScenario =
            uiBothActuallyEmpty &&
            (visibleFieldGroups?.length === 1) &&
            (visibleFieldGroups?.[0]?.Group === 'Processing Fee Warning');

        if (!atLeastOneFilled) {
            if (processingFeeOnlyScenario) {
                await redirectToPage(redirectPage, loanId);
                return;
            }
            showToastMsg('Please fill at least one required field.');
            return;
        }

        if (missingFields.length > 0) {
            showToastMsg(
                `The following required fields are missing or need update:<br>- ${missingFields.join('<br>- ')}`
            );
            return;
        }

        const onlyAppDateMessages = missingFields.length > 0 &&
            missingFields.every(msg =>
                msg === "Application Date is required." ||
                msg === "Application Date cannot be a future date."
            );

        if (onlyAppDateMessages) {
            return;
        }

        setLoadingText('Saving...');
        setLoading(true);
        try {
            if (updated.length > 0) {
                await fieldWriter(token, loanId, updated);
            }
            // collect base field ids from JSON Fields
            const baseFieldIds = config.FieldGroups.flatMap((group) =>
                (group.Fields?.filter((f) => f.id).map((f) => f.id)) || []
            );
            // ALSO collect ShowIf field ids so we can evaluate visibility
            const showIfFieldIds = config.FieldGroups
                .map(g => g.ShowIf?.field)
                .filter(Boolean);

            // build ids to read (loan type + 4788 + all base fields)
            const fieldIds = [
                ...new Set([
                    'CX.LOAN.TYPE',
                    '4788', // Lock Validation status (also used as button id)
                    '3238',
                    'LOCKRATE.CURRENTSTATUS',
                    '230',
                    '231',
                    'LCP.X1',
                    'LCP.X8',
                    'CX.LEGAL.ENTITY.TYPE',
                    "4460",
                    "300#2",
                    "300#3",
                    "300#4",
                    "300#5",
                    "CX.TPOC.OVERRIDE.VALIDATIONS",
                    ...baseFieldIds,
                    ...showIfFieldIds,
                    ...config.FieldGroups.flatMap(group => collectConditionFields(group.NotRequiredIf))
                ])
            ];

            const values = await fieldReader(token, fieldIds, loanId);

            let loanType = values['CX.LOAN.TYPE'];
            const stateCode = (values['14'] || '').trim();
            const tpoOrgId = (values['TPO.X15'] || '').trim();
            const tpoUserId = (values['TPO.X62'] || '').trim();
            const city = (values['12'] || '').trim();

            const overrideValidations =
                (values['CX.TPOC.OVERRIDE.VALIDATIONS'] || '')
                    .toString()
                    .trim()
                    .toUpperCase();

            const skipModules = overrideValidations === 'Y';
            setSkipModuleValidations(skipModules);

            let filteredGroups = config.FieldGroups.filter(
                (g) => !g.LoanType || g.LoanType === loanType
            );

            if (skipModules) {
                filteredGroups = filteredGroups.filter(
                    (g) => (g.Type || '').toLowerCase() !== 'module'
                );
            }

            const legalEntityType = (values['CX.LEGAL.ENTITY.TYPE'] || '').trim();

            // Run licensing gate and store result
            let status = {
                blocked: false,
                companyRequired: false,
                loRequired: false,
                companyHas: false,
                loHas: false,
                stateCode: '',
                stateName: '',
                reason: ''
            };

            if (!skipModules) {
                status = await runLicensingGateForPage(
                    loanType,
                    stateCode,
                    tpoOrgId,
                    tpoUserId,
                    city,
                    legalEntityType
                );
            }

            setLicenseStatus({ ...status, city });

            let closingFeeFulfilled = true;

            if (!skipModules && (values['CX.LOAN.TYPE'] || '').trim() === 'Consumer') {
                try {
                    const serviceOrders = await getServiceOrders(token, loanId);
                    closingFeeFulfilled = hasFulfilledClosingFeeOrder(serviceOrders);
                } catch (err) {
                    console.error('Closing Fee service order check failed:', err);
                    closingFeeFulfilled = false;
                }
            }

            setClosingFeeOrderFulfilled(closingFeeFulfilled);


            // NEW: If Business loan, hide "Commerical Loan App" group when the document already exists
            // if ((loanType || '').trim() === 'Business') {
            //     // Find the Business group (by name) and read its 'document' field from JSON
            //     const businessGroup = filteredGroups.find(g => g.Group === 'Commerical Loan App');
            //     const businessDocField = businessGroup?.Fields?.find(f => f.document);
            //     const docTitle = businessDocField?.document;

            //     if (docTitle) {
            //         const docId = await checkDocumentByAssignedEntity(token, loanId, docTitle);
            //         const businessDocMissing = !docId;

            //         // If document exists, remove the "Commerical Loan App" group from the page
            //         if (!businessDocMissing) {
            //             filteredGroups = filteredGroups.filter(g => g.Group !== 'Commerical Loan App');
            //         }
            //     }
            // }

            if (status.blocked) {
                filteredGroups = [
                    ...filteredGroups.filter(g => g.Group === 'Licensing'),
                    ...filteredGroups.filter(g => g.Group !== 'Licensing'),
                ];
            }

            setFieldValues(values);
            setPageConfig({ ...config, FieldGroups: filteredGroups });

            // Apply disclosure cutoff validation ONLY for Consumer loans
            loanType = (values['CX.LOAN.TYPE'] || '').toLowerCase();

            if (loanType === 'consumer' && values['CX.DISCLOSURE.GEN.DT']) {
                const disclosuredate = new Date(values['CX.DISCLOSURE.GEN.DT']);
                const today = new Date();

                const cutoffDate = new Date(disclosuredate.toISOString().split('T')[0]);
                const todayDate = new Date(today.toISOString().split('T')[0]);

                if (cutoffDate < todayDate) {
                    showToastMsg(
                        "The Initial Disclosures window is closed based on your application date.<br>Please update the application date if it's incorrect."
                    );
                    return;
                }
            }

            await redirectToPage(redirectPage, loanId);
        } catch (err) {
            console.error("Save failed", err);

            // Show only the error details string
            const message = err?.response?.data?.errors?.[0]?.details
                || err?.response?.data?.details
                || "Save failed. Please try again.";

            showToastMsg(message);
            setLoading(false);
            return;
        } finally {
            setLoading(false);
        }
    };

    const redirectToPage = async (page, loanId) => {
        const tpoAppObject = await window.elli.script.getObject("tpoApplication");

        const loanTypeValue = fieldValues["CX.LOAN.TYPE"]?.toLowerCase() || "";
        const loanOfficerNMLSID = fieldValues["3238"]?.trim(); // NMLS Field ID

        const isBusinessLoan = loanTypeValue.includes("business");
        const menuItems = isBusinessLoan ? menuItemsBP : menuItemsCP;

        // Find menu item
        let menuItem = menuItems.find(item => item.displayName === page || item.label === page);

        // Check subsections if not found in top-level
        if (!menuItem) {
            for (const parent of menuItems) {
                if (parent.subsections) {
                    const sub = parent.subsections.find(subItem => subItem.displayName === page || subItem.label === page);
                    if (sub) {
                        menuItem = sub;
                        break;
                    }
                }
            }
        }

        // Override CorelogicCredco → DUAL AUS for Business Loan + NMLS ID
        if (isBusinessLoan && loanOfficerNMLSID && menuItem?.label === "CorelogicCredco") {
            menuItem = menuItemsCP.find(item => item.label === "DUAL AUS"); // fallback to CP for DUAL AUS config
        }

        // Redirect to external page if extPageId is present
        if (menuItem?.extPageId) {
            await tpoAppObject.pageRedirect("ExternalLoanPage", {
                id: loanId,
                extpageid: menuItem.extPageId,
                pagename: menuItem.label
            });
        } else {
            // Default internal redirection
            await tpoAppObject.pageRedirect(menuItem.label, { id: loanId });
        }
    };

    const isMoneyField = (id) => id === 'NEWHUD.X139' || id === '1621';

    // count only differences
    const diffCount = lcFields.filter(
        (f) =>
            (f.currentLoanValue ?? '').toString().trim() !==
            (f.currentLockValue ?? '').toString().trim()
    ).length;

    const isProcessingFeeField = (fieldId) =>
        fieldId === 'NEWHUD.X139' || fieldId === '1621';


    const msgLower = (licenseStatus.message || '').toLowerCase();

    const isRestrictionMessage =
        msgLower.includes('not eligible for')

    const sectionHeaderText = isRestrictionMessage ? 'Location Restriction' : 'Licensing';

    return (
        <>
            <LoadingOverlay loading={loading} text={loadingText} />

            <Toast
                visible={toastVisible}
                type="warning"
                message={toastMessage}
                onClose={() => {
                    setShowToast(false);
                    setToastVisible(false);
                }}
                animationClass={toastAnimation}
            />

            {/* ZIP City/State selection modal */}
            {showZipPopup && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                    onClick={() => setShowZipPopup(false)}
                >
                    <div
                        className="bg-white rounded-lg p-6 w-96 shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Select City & State</h3>
                            <button
                                className="text-gray-500 hover:text-black"
                                onClick={() => setShowZipPopup(false)}
                            >
                                ×
                            </button>
                        </div>
                        <ul className="space-y-2">
                            {cityOptions.map((opt, idx) => (
                                <li key={idx}>
                                    <button
                                        onClick={() => {
                                            document.getElementById(`field-${zipSelection.cityId}`).value =
                                                opt.city;
                                            document.getElementById(`field-${zipSelection.stateId}`).value =
                                                opt.abbrev;
                                            setShowZipPopup(false);
                                        }}
                                        className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                                    >
                                        {opt.city}, {opt.abbrev}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">{pageName}</h2>

                    {!loading && (
                        <>
                            {licenseStatus?.blocked ? (
                                /* --- Show ONLY Licensing when blocked --- */
                                <div className="border border-blue-200 rounded bg-gray-50">
                                    <div className="section-header">{sectionHeaderText}</div>

                                    <div className="p-4 space-y-4">
                                        <div className="flex items-start gap-3">
                                            <div className="mt-1 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-700">
                                                Blocking
                                            </div>
                                            <div>
                                                <div className="text-base font-semibold">
                                                    {isRestrictionMessage ? 'Location restriction' : 'Licensing verification required'}
                                                    {isRestrictionMessage && licenseStatus.city
                                                        ? ` for ${licenseStatus.city}, ${licenseStatus.stateCode}`
                                                        : licenseStatus.stateCode
                                                            ? ` for ${licenseStatus.stateCode} state`
                                                            : ''}
                                                </div>

                                                <p className="text-base text-red-700 mt-1 font-medium"
                                                    dangerouslySetInnerHTML={{ __html: licenseStatus.message }}>
                                                </p>
                                            </div>
                                        </div>

                                        {!isRestrictionMessage && (
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div className="border rounded p-3 bg-white">
                                                    <div className="text-sm font-medium mb-1">Company / Broker License</div>
                                                    <div className="text-sm">
                                                        {licenseStatus.companyRequired ? (
                                                            licenseStatus.companyHas ? (
                                                                <span className="inline-flex items-center text-green-700">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-green-600" /> Found
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center text-red-700">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-red-600" /> Missing
                                                                </span>
                                                            )
                                                        ) : (
                                                            <span className="inline-flex items-center text-gray-600">
                                                                <span className="w-2 h-2 rounded-full inline-block mr-2 bg-gray-400" /> Not Required
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="border rounded p-3 bg-white">
                                                    <div className="text-sm font-medium mb-1">Loan Originator License</div>
                                                    <div className="text-sm">
                                                        {licenseStatus.loRequired ? (
                                                            licenseStatus.loHas ? (
                                                                <span className="inline-flex items-center text-green-700">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-green-600" /> Found
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center text-red-700">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-red-600" /> Missing
                                                                </span>
                                                            )
                                                        ) : (
                                                            <span className="inline-flex items-center text-gray-600">
                                                                <span className="w-2 h-2 rounded-full inline-block mr-2 bg-gray-400" /> Not Required
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            ) : (
                                /* --- Normal module rendering when NOT blocked --- */
                                pageConfig?.FieldGroups
                                    .filter((group) => group.Type === 'Module')
                                    .filter((group) => shouldShowGroup(group))
                                    .filter((group) => !isModuleGroupCleared(group))
                                    .map((group, index) => (
                                        <div key={index} className="border border-blue-200 rounded bg-gray-50 mt-5">
                                            <div className="section-header">{group.Group}</div>
                                            {(() => {

                                                if (group.Group === 'Rate Lock — Processing') {
                                                    const finalNote = getLockStatusNote(fieldValues['LOCKRATE.CURRENTSTATUS']);

                                                    return (
                                                        <div
                                                            className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line"
                                                        >
                                                            {finalNote}
                                                        </div>
                                                    );
                                                }

                                                // Loan program blocked for this org: name the loan's product in the note
                                                if (group.Group === 'Loan Program Not Available') {
                                                    const productName = getBlockedLoanProgramName(fieldValues[group.ShowIf?.field], fieldValues['1401']);

                                                    return (
                                                        <div
                                                            className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line"
                                                            dangerouslySetInnerHTML={{ __html: (group.Notes || '').split('{{LOAN_PROGRAM}}').join(productName) }}
                                                        />
                                                    );
                                                }

                                                // Default behavior: show whatever is in group.Notes (unchanged)
                                                if (group.Notes) {
                                                    return (
                                                        <div
                                                            className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line"
                                                            dangerouslySetInnerHTML={{ __html: group.Notes }}
                                                        />
                                                    );
                                                }

                                                return null;
                                            })()}

                                            {/* Special: Lock Comparison body */}
                                            {group.Group === 'Lock Comparison' ? (
                                                <div className="p-4 space-y-3">
                                                    <div className="flex items-center justify-between pb-2 flex-wrap gap-4">
                                                        <div className="flex flex-wrap items-center gap-4 text-sm">
                                                            <p className="text-gray-500">Field Changes: {diffCount}</p>
                                                            <p>Lock Date: {lcLockDate || '-'}</p>
                                                            <p>Expiration: {lcExpire || '-'}</p>
                                                            {lcStatus && (
                                                                <p className="text-red-600 font-semibold">{lcStatus}</p>
                                                            )}
                                                        </div>

                                                    </div>

                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm border-collapse mt-2">
                                                            <thead className="text-left bg-gray-100">
                                                                <tr>
                                                                    <th className="p-2 border w-1/4">Field Name</th>
                                                                    <th className="p-2 border w-1/4">Locked Value</th>
                                                                    <th className="p-2 border w-1/4">Current Loan Value</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {lcFields
                                                                    .filter(
                                                                        (f) =>
                                                                            (f.currentLoanValue ?? '').toString().trim() !==
                                                                            (f.currentLockValue ?? '').toString().trim()
                                                                    )
                                                                    .map((f) => (
                                                                        <tr key={f.lockFieldId || f.description}>
                                                                            <td className="p-2 border font-medium">
                                                                                {f.description}
                                                                            </td>
                                                                            <td className="p-2 border">
                                                                                {f.currentLockValue || '-'}
                                                                            </td>
                                                                            <td className="p-2 border bg-yellow-100 font-semibold text-red-700">
                                                                                {f.currentLoanValue || '-'}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                {diffCount === 0 && (
                                                                    <tr>
                                                                        <td className="p-2 border italic text-gray-600" colSpan={3}>
                                                                            All fields match the current lock.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <button
                                                            type="button"
                                                            className="mt-10 btn em-btn-primary"
                                                            id="field-4788"
                                                            onClick={() =>
                                                                redirectToPage('Product Pricing & Lock', loanId)
                                                            }
                                                        >
                                                            Re-Price
                                                        </button>
                                                    </div>
                                                </div>

                                            ) : (
                                                // Default module: render its buttons
                                                <div className="grid md:grid-cols-2 gap-6 p-4">
                                                    {group.Fields?.map((field) => (
                                                        <div key={field.id}>
                                                            {/* Hidden mirror input kept for consistency (no save) */}
                                                            <input
                                                                type="text"
                                                                className="hidden"
                                                                id={`field-${field.id}`}
                                                                defaultValue={fieldValues[field.id]}
                                                                readOnly
                                                            />
                                                            <button
                                                                type="button"
                                                                className="mt-2 btn em-btn-tertiary"
                                                                onClick={() => redirectToPage(field.pagetoRedirect, loanId)}
                                                            >
                                                                {field.name}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                            }
                                        </div>
                                    ))
                            )}

                            {/* Only when all modules that should show are cleared, render FIELD groups */}
                            {allModulesCleared && (
                                <>
                                    <div className="flex justify-between items-center mt-4 mb-2">
                                        {showAllFieldsRequiredNote ? (
                                            <p className="text-sm text-red-600 italic mb-2">
                                                * These fields are required to {pageName}.
                                            </p>
                                        ) : (
                                            <p className="text-sm text-red-600 italic mb-2">
                                                &nbsp;
                                            </p>
                                        )}


                                        <div className="flex gap-2">
                                            {canContinueToSendDisclosures && (
                                                <button
                                                    onClick={() => redirectToPage(redirectPage, loanId)}
                                                    className="btn em-btn-secondary"
                                                >
                                                    {continueDisclosureButtonText}
                                                </button>
                                            )}

                                            <button onClick={handleSave} className="btn em-btn-primary">
                                                Save
                                            </button>
                                        </div>
                                    </div>

                                    {pageConfig?.FieldGroups
                                        .filter((group) => group.Type === 'Field')
                                        .filter((group) => shouldShowGroup(group))
                                        .map((group, index) => (
                                            <div key={index} className="border border-blue-200 rounded bg-gray-50 mt-5">
                                                <div className="section-header">{group.Group}</div>
                                                {group.Group !== 'Processing Fee Warning' && group.Notes && (
                                                    <div className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line">
                                                        {group.Notes}
                                                    </div>
                                                )}

                                                {group.Group === 'Processing Fee Warning' && (
                                                    <div className="px-4 pt-4">
                                                        {processingFeeBothFilled ? (
                                                            // HARDSTOP
                                                            <div className="flex items-start gap-3 border border-red-300 bg-red-50 rounded p-3">
                                                                <div className="mt-0.5 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-red-200 text-red-900">
                                                                    Blocking
                                                                </div>
                                                                <div className="text-sm text-red-900 font-medium">
                                                                    You cannot have a Processing Fee and a Third Party Processing Fee. Please remove one before proceeding to disclosures.
                                                                    Processing payable to your company. Third Party Processing is paid to an outside processing company.
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            // WARNING ONLY (both empty)
                                                            <div className="flex items-start gap-3 border border-yellow-300 bg-yellow-50 rounded p-3">
                                                                <div className="mt-0.5 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-yellow-200 text-yellow-900">
                                                                    Warning
                                                                </div>
                                                                <div className="text-sm text-yellow-900">
                                                                    You have not entered a Processing Fee (1621) or a Third Party Processing Fee (NEWHUD.X139).
                                                                    Please return to the fee entry and enter an amount as applicable.
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}


                                                <div className="grid md:grid-cols-2 gap-6 p-4">
                                                    {group.Fields
                                                        .filter((field) => !field.hidden)
                                                        .filter((field) => {
                                                            // Processing Fee Warning: show BOTH fields when group is visible
                                                            if (group.Group === 'Processing Fee Warning') return true;

                                                            // If group is forced visible via ShowIf, show all its fields
                                                            if (group.ShowIf) return true;

                                                            // If the field is conditionally required via JSON (requiredIf),
                                                            // hide the field when the requiredIf condition is not met.
                                                            if (field.requiredIf && field.requiredIf.field) {
                                                                const triggerFieldId = field.requiredIf.field;
                                                                const triggerOp = field.requiredIf.operator;
                                                                const triggerVal = fieldValues[triggerFieldId];

                                                                // Only support 'notEmpty' / 'empty' triggers here (matches your JSON pattern)
                                                                if (triggerOp === 'notEmpty' && isEmptyField(triggerFieldId, triggerVal)) {
                                                                    // condition not met -> field is not required and should not be shown
                                                                    return false;
                                                                }
                                                                if (triggerOp === 'empty' && !isEmptyField(triggerFieldId, triggerVal)) {
                                                                    // condition not met -> field should not be shown
                                                                    return false;
                                                                }
                                                            }
                                                            const value = fieldValues[field.id];
                                                            return isEmptyField(field.id, value) || isInvalidByValidate(field);
                                                        })
                                                        .map((field) => (
                                                            <div key={field.id}>
                                                                {field.datatype !== 'button' && (
                                                                    <label
                                                                        className="form-label mb-1"
                                                                        htmlFor={`field-${field.id}`}
                                                                    >
                                                                        {field.name}

                                                                        {/* show * only when NOT processing fee warning fields */}
                                                                        {!isProcessingFeeField(field.id) && (
                                                                            <span className="text-red-600"> *</span>
                                                                        )}
                                                                    </label>
                                                                )}

                                                                {field.datatype === 'State' ? (
                                                                    <select
                                                                        className="form-input w-full text-sm border rounded p-1"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue=""
                                                                    >
                                                                        <option value="">Select State</option>
                                                                        {US_STATES.map((state) => (
                                                                            <option key={state.abbrev} value={state.abbrev}>
                                                                                {state.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : field.datatype === 'Date' ? (
                                                                    <input
                                                                        type="date"
                                                                        className="form-input-medium"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue={
                                                                            fieldValues[field.id]
                                                                                ? new Date(fieldValues[field.id])
                                                                                    .toISOString()
                                                                                    .split('T')[0]
                                                                                : ''
                                                                        }
                                                                    />
                                                                ) : field.datatype === 'dropdown' ? (
                                                                    <select
                                                                        className="form-input w-full text-sm border rounded p-1"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue={fieldValues[field.id] || ''}
                                                                    >
                                                                        <option value="">Select</option>
                                                                        {/* resolveFieldOptions turns canonical loan-program
                                                                            codes into this org's product names and drops the
                                                                            products it does not carry. */}
                                                                        {resolveFieldOptions(field).map((opt) => (
                                                                            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                                                {opt.text}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : field.datatype === 'button' ? (
                                                                    <div>
                                                                        <input
                                                                            type="text"
                                                                            className="hidden"
                                                                            id={`field-${field.id}`}
                                                                            defaultValue={fieldValues[field.id]}
                                                                            onBlur={() =>
                                                                                field.datatype === 'Zip' && handleZipBlur(field)
                                                                            }
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="mt-2 btn em-btn-tertiary"
                                                                            onClick={() =>
                                                                                redirectToPage(field.pagetoRedirect, loanId)
                                                                            }
                                                                        >
                                                                            {field.name}
                                                                        </button>
                                                                    </div>
                                                                ) : field.datatype === 'decimal' ? (
                                                                    <input
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        pattern="^\d+(\.\d+)?$"
                                                                        className="form-input"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue={fieldValues[field.id]}
                                                                        onInput={(e) => {
                                                                            e.target.value = filterDecimalKeystrokes(e.target.value);
                                                                        }}
                                                                        onBlur={(e) => {
                                                                            const money = isMoneyField(field.id);

                                                                            e.target.value = normalizeDecimal(e.target.value, {
                                                                                min: 0,
                                                                                max: money ? 999999999 : 100,
                                                                                decimals: money ? 2 : 3
                                                                            });
                                                                        }}

                                                                        title="Enter a decimal value"
                                                                    />
                                                                ) : (
                                                                    <input
                                                                        type="text"
                                                                        className="form-input"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue={fieldValues[field.id]}
                                                                        onBlur={() =>
                                                                            field.datatype === 'Zip' && handleZipBlur(field)
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        ))}

                                    <div className="text-right mt-8">
                                        <div className="gap-2">
                                            {canContinueToSendDisclosures && (
                                                <button
                                                    type="button"
                                                    onClick={() => redirectToPage(redirectPage, loanId)}
                                                    className="btn em-btn-secondary"
                                                >
                                                    {continueDisclosureButtonText}
                                                </button>
                                            )}

                                            <button onClick={handleSave} className="btn em-btn-primary">
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default DisclosurePageRenderer;